import { createBrowserRouter, Navigate } from "react-router";
import { RequireAuth } from "./components/RequireAuth";
import { RequireTier } from "./components/RequireTier";
import { RequireFinance } from "./components/RequireFinance";
import { RequirePerm } from "./components/RequirePerm";
import { Shell } from "./components/Shell";
import { LoginPage } from "./pages/LoginPage";
import { SignDeskPage } from "./pages/SignDeskPage";
import { RequestDetailPage } from "./pages/RequestDetailPage";
import { RequestsPage } from "./pages/RequestsPage";
import { IncidentsPage } from "./pages/IncidentsPage";
import { IncidentDetailPage } from "./pages/IncidentDetailPage";
import { ConsultationsPage } from "./pages/ConsultationsPage";
import { ConsultationDetailPage } from "./pages/ConsultationDetailPage";
import { DistrictAuditPage } from "./pages/district/DistrictAuditPage";
import { DistrictCatalogPage } from "./pages/district/DistrictCatalogPage";
import { DistrictRevenuePage } from "./pages/district/DistrictRevenuePage";
import { DistrictStaffPage } from "./pages/district/DistrictStaffPage";
import { DashboardPage } from "./pages/dashboard/DashboardPage";
import { KeepQueryRedirect } from "./components/KeepQueryRedirect";
import { AnnouncementsPage } from "./pages/system/AnnouncementsPage";
import { TasaengsPage } from "./pages/system/TasaengsPage";
import { SystemSecurityPage } from "./pages/system/SystemSecurityPage";
import { SystemUsersPage } from "./pages/system/SystemUsersPage";
import { ServicesPage } from "./pages/system/ServicesPage";
import { HomeRedirect } from "./pages/HomeRedirect";
import { FinanceReportPage } from "./pages/finance/FinanceReportPage";
import { ExpensesPage } from "./pages/finance/ExpensesPage";
import { FINANCE_REPORT_PERMS } from "./lib/permissions";

/**
 * เส้นทางแยกตาม namespace ของระดับคอนโซล (ADR-0016)
 *
 * `/district/*` `/province/*` `/system/*` คือจอเฉพาะระดับ ส่วนจอที่ทุกระดับใช้ร่วมกัน
 * (คิวคำร้อง) อยู่นอก namespace เพราะ API กรองด้วย scope ให้อยู่แล้ว
 * — แยก path ตามระดับจะทำให้ต้องมีสามหน้าจอที่โค้ดเหมือนกันเป๊ะ
 */
const FINANCE_DENIED = "ລາຍງານການເງິນ ແລະ ລາຍຈ່າຍ ສຳລັບຕຳແໜ່ງທີ່ມີສິດເບິ່ງ Dashboard ການເງິນ ຫຼື ສິດ FIN-08 ເທົ່ານັ້ນ";

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <Shell />,
        children: [
          { index: true, element: <HomeRedirect /> },
          // เส้นทางเดิมก่อนแยกระดับ — คงไว้ไม่ให้ bookmark เก่าพัง
          { path: "/overview", element: <Navigate to="/dashboard" replace /> },
          // ພາບລວມເມືອງ + ພາບລວມທົ່ວແຂວງ ถูกรวมเป็น Dashboard เดียว — ลิงก์เก่าพาไปพร้อมตัวกรองเดิม
          { path: "/district", element: <KeepQueryRedirect to="/dashboard" /> },
          { path: "/province", element: <KeepQueryRedirect to="/dashboard" /> },
          // หน้าแก้แคตตาล็อกของ super admin ถูกรวมเข้า /district/catalog แล้ว
          { path: "/document-types", element: <Navigate to="/district/catalog" replace /> },
          {
            element: <RequireTier tier="district" />,
            children: [
              { path: "/dashboard", element: <DashboardPage /> },
              { path: "/district/catalog", element: <DistrictCatalogPage /> },
              {
                path: "/district/staff",
                element: (
                  <RequirePerm perm="CFG-11" message="ຈັດການບັນຊີພະນັກງານ ຕ້ອງມີສິດ CFG-11 (ສ້າງ/ປິດບັນຊີ ຕຳແໜ່ງຕ່ຳກວ່າ)">
                    <DistrictStaffPage />
                  </RequirePerm>
                ),
              },
              {
                path: "/district/revenue",
                element: (
                  <RequireFinance>
                    <DistrictRevenuePage />
                  </RequireFinance>
                ),
              },
              { path: "/district/audit", element: <DistrictAuditPage /> },
              // ລາຍງານການເງິນ + ລາຍຈ່າຍ (ADR-0034) — DSH-04/07/10 หรือ FIN-08; server ตอบ 403 อีกชั้น
              {
                path: "/reports/finance",
                element: (
                  <RequirePerm perm={FINANCE_REPORT_PERMS} message={FINANCE_DENIED}>
                    <FinanceReportPage />
                  </RequirePerm>
                ),
              },
              {
                path: "/finance/expenses",
                element: (
                  <RequirePerm perm={FINANCE_REPORT_PERMS} message={FINANCE_DENIED}>
                    <ExpensesPage />
                  </RequirePerm>
                ),
              },
            ],
          },
          { path: "/requests", element: <RequestsPage /> },
          // ລໍເຊັນ ແລະ ປະທັບຕາ (mockup v2 work_sign.js) — ผู้เซ็นเท่านั้น หน้าเช็คสิทธิ์เอง และ server เช็คซ้ำตอนเซ็น
          { path: "/requests/sign", element: <SignDeskPage /> },
          { path: "/requests/:id", element: <RequestDetailPage /> },
          // ບໍລິການເສີມ (ADR-0027) — ทุกระดับใช้ร่วมกัน API กรอง scope ให้; นัดปรึกษาเปิดเนื้อหาเฉพาะผู้ตรวจ
          // (role อื่นเห็นแค่สถิติ — ConsultationsPage ตัดสินจาก role แล้ว API ตอบ 403 อีกชั้น)
          { path: "/incidents", element: <IncidentsPage /> },
          { path: "/incidents/:id", element: <IncidentDetailPage /> },
          { path: "/consultations", element: <ConsultationsPage /> },
          { path: "/consultations/:id", element: <ConsultationDetailPage /> },
          {
            element: <RequireTier tier="system" />,
            children: [
              { path: "/system/security", element: <SystemSecurityPage /> },
              { path: "/users", element: <SystemUsersPage /> },
              { path: "/system/tasaengs", element: <TasaengsPage /> },
              { path: "/system/announcements", element: <AnnouncementsPage /> },
              { path: "/system/services", element: <ServicesPage /> },
            ],
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);
